import {
  useQuery,
  useMutation,
  useQueryClient,
  QueryFunction,
} from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import {
  ResourceDocument,
  ResourceDocumentConnection,
  ResourceOperationResponse,
  CreateResourceDocumentRequest,
  UpdateResourceDocumentRequest,
  ResourceDocumentFilters,
} from './types'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

export const useResourceDocuments = (filters?: ResourceDocumentFilters) => {
  const fetchWithAuth = useFetchWithAuth()

  const getDocuments: QueryFunction<ResourceDocumentConnection> = async () => {
    const params = new URLSearchParams()

    if (filters?.limit) params.append('limit', filters.limit.toString())
    if (filters?.cursor) params.append('cursor', filters.cursor)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.category_id) params.append('category_id', filters.category_id)
    if (filters?.featured !== undefined)
      params.append('featured', filters.featured.toString())
    if (filters?.search) params.append('search', filters.search)

    const url = `${API_URL}/api/resources/documents${
      params.toString() ? `?${params}` : ''
    }`

    const response = await fetchWithAuth(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })

    return response
  }

  return useQuery({
    queryKey: ['resource-documents', filters] as const,
    queryFn: getDocuments,
    retry: false,
    staleTime: 2 * 60 * 1000, // 2 minutes (shorter than categories since content changes more frequently)
  })
}

export const useResourceDocument = (documentId: string) => {
  const fetchWithAuth = useFetchWithAuth()

  const getDocument: QueryFunction<ResourceOperationResponse> = async () => {
    const response = await fetchWithAuth(
      `${API_URL}/api/resources/documents/${documentId}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    )

    return response
  }

  return useQuery({
    queryKey: ['resource-document', documentId] as const,
    queryFn: getDocument,
    retry: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!documentId,
  })
}

export const useResourceDocumentBySlug = (slug: string) => {
  const fetchWithAuth = useFetchWithAuth()

  const getDocumentBySlug: QueryFunction<ResourceOperationResponse> = async () => {
    const response = await fetchWithAuth(
      `${API_URL}/api/resources/documents/slug/${slug}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    )

    return response
  }

  return useQuery({
    queryKey: ['resource-document-by-slug', slug] as const,
    queryFn: getDocumentBySlug,
    retry: false,
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!slug,
  })
}

export const useCreateResourceDocumentMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (payload: CreateResourceDocumentRequest) => {
      const response = await fetchWithAuth(
        `${API_URL}/api/resources/documents`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      )

      return response as ResourceOperationResponse
    },
    onSuccess: () => {
      // Invalidate documents lists
      queryClient.invalidateQueries({ queryKey: ['resource-documents'] })
    },
  })

  return [
    async (variables: {
      variables: { payload: CreateResourceDocumentRequest }
    }) => {
      return mutation.mutateAsync(variables.variables.payload)
    },
    { loading: mutation.isPending, error: mutation.error },
  ] as const
}

export const useUpdateResourceDocumentMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (variables: {
      documentId: string
      payload: UpdateResourceDocumentRequest
    }) => {
      const { documentId, payload } = variables

      const response = await fetchWithAuth(
        `${API_URL}/api/resources/documents/${documentId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      )

      return response as ResourceOperationResponse
    },
    onSuccess: (data, variables) => {
      // Invalidate documents lists
      queryClient.invalidateQueries({ queryKey: ['resource-documents'] })
      // Invalidate specific document
      queryClient.invalidateQueries({
        queryKey: ['resource-document', variables.documentId],
      })
      // Invalidate document by slug if we have the updated document data
      if (data.document?.slug) {
        queryClient.invalidateQueries({
          queryKey: ['resource-document-by-slug', data.document.slug],
        })
      }
    },
  })

  return [
    async (variables: {
      variables: { documentId: string; payload: UpdateResourceDocumentRequest }
    }) => {
      return mutation.mutateAsync({
        documentId: variables.variables.documentId,
        payload: variables.variables.payload,
      })
    },
    { loading: mutation.isPending, error: mutation.error },
  ] as const
}

export const useDeleteResourceDocumentMutation = () => {
  const fetchWithAuth = useFetchWithAuth()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: async (documentId: string) => {
      const response = await fetchWithAuth(
        `${API_URL}/api/resources/documents/${documentId}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
        }
      )

      return response as ResourceOperationResponse
    },
    onSuccess: () => {
      // Invalidate documents lists
      queryClient.invalidateQueries({ queryKey: ['resource-documents'] })
    },
  })

  return [
    async (variables: { variables: { documentId: string } }) => {
      return mutation.mutateAsync(variables.variables.documentId)
    },
    { loading: mutation.isPending, error: mutation.error },
  ] as const
}

// Convenience hooks for different use cases
export const usePublishedResourceDocuments = (
  filters?: Omit<ResourceDocumentFilters, 'status'>
) => {
  return useResourceDocuments({ ...filters, status: 'PUBLISHED' })
}

export const useFeaturedResourceDocuments = (
  filters?: Omit<ResourceDocumentFilters, 'featured'>
) => {
  return useResourceDocuments({ ...filters, featured: true })
}

export const useResourceDocumentsByCategory = (
  categoryId: string,
  filters?: Omit<ResourceDocumentFilters, 'category_id'>
) => {
  return useResourceDocuments({ ...filters, category_id: categoryId })
}

export const useSearchResourceDocuments = (
  searchQuery: string,
  filters?: Omit<ResourceDocumentFilters, 'search'>
) => {
  return useResourceDocuments({ ...filters, search: searchQuery })
}
